import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { apiUrl, SITE_KEY_V2, SITE_KEY_V3 } from "config/settings";
import { setSelectedPlugin, setUser } from "store/userSettingsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CustomButton from "components/CustomButton";
import { setIsSystem, setShowLoading, setShowMassage } from "store/sysSlice";
import NullCheck from "components/NullCheck";
import ReCAPTCHA from "react-google-recaptcha";
import useDataManager from "hooks/useDataManager";
import { deleteCookie, isAppleDevice, setCookie } from "config/functions";
import TwoFactorAuth from "./TwoFactorAuth";
import usePwa from "hooks/usePwa";
import BlackListDialog from "./BlackListDialog";
import useElectron from "hooks/useElectron";
import {
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import { ELECTRON_LAB_ONLY_ROLES } from "config/roles";
import { PLUGIN_PROD_LIST } from "config/const";

const Login = () => {
  const reRef = useRef(null);
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const { selectBiz, getInitData } = useDataManager()
  const { isElectron, isElectronSystem } = useElectron();
  const { installApp, checkPWA, showInstallButton } = usePwa();
  const { bt } = useBizTypeTranslation();
  const { userName } = useParams();
  const { handleSubmit, control } = useForm();
  const isLogin = useSelector((state) => state?.userSettings?.isLogin);

  const [anim, setAnim] = useState(!isLogin);
  const [showPassword, setShowPassword] = useState(false);
  const [successCaptcha, setSuccessCaptcha] = useState(false);
  const [twoFactorAuth, setTwoFactorAuth] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState({ open: false, empId: null });

  const [openBlackList, setOpenBlackList] = useState(false);
  // const [openDBErrorDialog, setOpenDBErrorDialog] = useState(true);

  const getUser = localStorage.getItem("userName");
  const recaptcha_v2 = localStorage.getItem("recaptcha");
  const [hasRecaptcha_v2, setHasRecaptcha_v2] = useState(recaptcha_v2);

  useEffect(() => {
    if (!isLogin) {
      localStorage.removeItem("user");
      localStorage.removeItem("biz");
      dispatch(setSelectedPlugin(null))
      deleteCookie("lab");
      deleteCookie("authAnalytics");
    }

    if (!hasRecaptcha_v2) {
      setSuccessCaptcha(true);
    }

    dispatch(setShowLoading(false));
  }, []);

  const onSubmit = async (values) => {
    if ((userName && !values?.userName) || !values?.passWord || !successCaptcha)
      return;

    if (twoFactorAuth && !values?.twoFactorCode)
      return dispatch(setShowMassage({ massage: "TWO_FACTOR_CODE_REQUIRED", type: "error" }));

    dispatch(setShowLoading(true));

    if (values?.rememberMe)
      localStorage.setItem("userName", userName ?? values?.userName);
    else
      localStorage.removeItem("userName");

    const reToken = await reRef?.current?.execute();
    let isLab = false

    const user = {
      userName: userName ?? values?.userName,
      passWord: values?.passWord,
      twoFactorCode: twoFactorAuth ? values?.twoFactorCode : null,
      reToken: reToken,
    };

    const resElectronSystem = await isElectronSystem()

    if (resElectronSystem?.ok) {
      setCookie("sys", resElectronSystem?.message, 30);
      dispatch(setIsSystem(true));
      isLab = true;
    }

    axios.post(`${apiUrl}/employees/login`, user).then((response) => {
      if (response?.data?.hasTwoFactor) {
        setTwoFactorCode({ open: true, empId: response?.data?.empId })
        setTwoFactorAuth(true)
        setHasRecaptcha_v2(false);
        dispatch(setShowLoading(false));
        return
      }

      const userRes = response?.data?.user;
      const findMainBiz = userRes?.business?.find(b => b?.isMain)

      if (!isLab && ELECTRON_LAB_ONLY_ROLES.includes(findMainBiz?.userRole)) {
        dispatch(setShowLoading(false));
        setTwoFactorAuth(false)
        setTwoFactorCode({ open: false, empId: null })
        return dispatch(setShowMassage({ massage: "USER_PERMISSION_ERROR", type: "error" }));
      }

      let token = response.headers.get("Authorization");

      setAnim(false);
      setCookie("authAnalytics", token, 30);

      localStorage.removeItem("recaptcha");
      dispatch(setShowMassage({ massage: "LOGIN_SUCCESS", type: "success" }));


      localStorage.setItem("user", JSON.stringify(userRes));
      dispatch(setUser({ ...userRes, userRole: findMainBiz?.userRole, department: findMainBiz?.department, }));

      selectBiz(findMainBiz?.id, true)
      const findedPlugin = PLUGIN_PROD_LIST?.find(p => findMainBiz?.plugins?.some(pl => pl === p?.sysName))?.sysName;
      dispatch(setSelectedPlugin(findedPlugin || findMainBiz?.plugins[0]));
      getInitData(findMainBiz?.userRole)
      dispatch(setShowLoading(false));
      navigate("/Dashboard")
    })
      .catch((error) => {
        dispatch(setShowLoading(false));
        if (error?.response?.data?.id === -151) {
          setOpenBlackList(true)
          return
        }

        if (!hasRecaptcha_v2 && !twoFactorAuth)
          setSuccessCaptcha(false);
        if (!twoFactorAuth) {
          setHasRecaptcha_v2(true);
          localStorage.setItem("recaptcha", "1");
        }

        dispatch(setShowMassage({ massage: "LOGIN_ERROR", type: "error" }));
      });
  };

  return (
    <Stack
      height="100%"
      width="100%"
      justifyContent="center"
      alignItems="center"
      sx={{ overflow: "hidden", scrollbarWidth: "none" }}
    >
      <Zoom in={anim}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack
            padding={3}
            spacing={3}
            bgcolor="white"
            borderRadius={2}
            border="3px solid black"
          >
            <Typography textAlign="center" variant="h6" fontWeight="bold">
              {bt("LOGIN")}
            </Typography>

            <Stack spacing={2}>
              <NullCheck valueForCheck={!userName}>
                <Controller
                  name="userName"
                  control={control}
                  defaultValue={getUser ?? ""}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      defaultValue={getUser ?? ""}
                      placeholder={bt("USER_NAME")}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
              </NullCheck>

              <Stack spacing={1}>
                <Controller
                  name="passWord"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      type={showPassword ? "text" : "passWord"}
                      placeholder={bt("PASSWORD")}
                      value={value}
                      onChange={onChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((data) => !data)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                <Controller
                  name="rememberMe"
                  control={control}
                  defaultValue={getUser ? true : false}
                  render={({ field: { onChange, value } }) => (
                    <Stack alignItems="center" direction="row" gap={0.5}>
                      <Checkbox
                        defaultChecked={getUser ? true : false}
                        sx={{ padding: 0 }}
                        disableRipple
                        checke={value}
                        onClick={onChange}
                        size="small"
                      />
                      <Typography fontSize={12}>{bt("REMEMBER_ME")}</Typography>
                    </Stack>
                  )}
                />
              </Stack>
            </Stack>

            <NullCheck valueForCheck={hasRecaptcha_v2}>
              <ReCAPTCHA
                sitekey={SITE_KEY_V2}
                size="normal"
                onChange={() => setSuccessCaptcha(true)}
              />
            </NullCheck>

            <CustomButton title="CONNECT" submit />

            <Stack alignItems="center">
              <Typography fontSize={12}>
                This site is protected by reCAPTCHA and the Google
              </Typography>
              <Typography fontSize={12}>
                <a href="https://policies.google.com/privacy">Privacy Policy</a>{" "}
                and
                <a href="https://policies.google.com/terms">
                  Terms of Service
                </a>{" "}
                apply.
              </Typography>

              <ReCAPTCHA
                style={{ visibility: "hidden" }}
                sitekey={SITE_KEY_V3}
                ref={reRef}
                size="invisible"
              />
            </Stack>
          </Stack>

          <NullCheck valueForCheck={!isAppleDevice() && showInstallButton && !checkPWA() && !isElectron()}>
            <Stack
              padding={3}
              spacing={3}
              marginTop={1}
              bgcolor="white"
              borderRadius={2}
              border="3px solid black"
            >
              <CustomButton title="INSTALL_APP" onClick={installApp} />
            </Stack>
          </NullCheck>

          <NullCheck valueForCheck={twoFactorCode?.open}>
            <TwoFactorAuth
              data={twoFactorCode}
              setData={setTwoFactorCode}
              setTwoFactorAuth={setTwoFactorAuth}
              control={control}
              onSubmit={() => handleSubmit(onSubmit)()}
            />
          </NullCheck>
        </form>
      </Zoom>

      <NullCheck valueForCheck={openBlackList}>
        <BlackListDialog open={openBlackList} setOpen={setOpenBlackList} />
      </NullCheck>

      {/* <NullCheck valueForCheck={openDBErrorDialog}>
        <DBErrorDialog
          open={openDBErrorDialog}
          setOpen={setOpenDBErrorDialog}
        />
      </NullCheck> */}
    </Stack>
  );
};

export default Login;
